<script setup></script>

<template>
  <section id="technologies" class="sections-technologies">
    <div class="sections-technologies__container container">
      <div
        class="sections-technologies__radial sections-technologies__radial--1"
      ></div>

      <div
        class="sections-technologies__radial sections-technologies__radial--2"
      ></div>

      <div
        class="sections-technologies__radial sections-technologies__radial--3"
      ></div>

      <div class="sections-technologies__body">
        <CSpriteIcon
          v-for="item in 8"
          :key="item"
          class="sections-technologies__icon sections-technologies__icon--plus"
          :class="`sections-technologies__icon--plus--${item}`"
          name="plus"
        />

        <UiCaptionUnderline
          caption="Technologies"
          class="sections-technologies__caption"
        />

        <h2 class="sections-technologies__title">
          <span
            class="sections-technologies__font sections-technologies__font--title"
          >
            Systems that think ahead
          </span>
        </h2>

        <p
          class="sections-technologies__paragraph sections-technologies__paragraph--1"
        >
          <span
            class="sections-technologies__font sections-technologies__font--paragraph"
          >
            Automation and AI are our trusted travel companions — from real-time
            pricing to intelligent routing and dynamic personalized journey
            logic. Every solution is designed to reduce manual overhead, surface
            insights faster, and keep operations running smoothly without
            constant intervention.
          </span>
        </p>

        <p
          class="sections-technologies__paragraph sections-technologies__paragraph--2"
        >
          <span
            class="sections-technologies__font sections-technologies__font--paragraph"
          >
            We don’t just follow trends — we implement technologies that make
            sense for your mission. Whether the product requires fast API-driven
            interactions, complex backend workflows, or interactive user
            interfaces, we choose the right tools for the job.
          </span>
        </p>

        <p
          class="sections-technologies__paragraph sections-technologies__paragraph--3"
        >
          <span
            class="sections-technologies__font sections-technologies__font--paragraph"
          >
            Our stack includes <strong>GoLang, Python, PHP/Laravel</strong> on
            the backend, and <strong>VueJS</strong> on the frontend and
            <strong>React Native</strong> for mobile — giving us the flexibility
            to build adaptive, high-performance systems optimized for any device
            or environment.
          </span>
        </p>

        <p
          class="sections-technologies__paragraph sections-technologies__paragraph--4"
        >
          <span
            class="sections-technologies__font sections-technologies__font--paragraph"
          >
            <strong>
              We approach every project with a focus on efficiency, scalability,
              and future-readiness — so platforms keep working as businesses
              grow.
            </strong>
          </span>
        </p>

        <div class="sections-technologies__footer">
          <CSpriteIcon
            name="skewed_grid_bullet"
            class="sections-technologies__icon sections-technologies__icon--grid-bullet"
          />
        </div>

        <CPicture
          name="centurio_tech"
          class="sections-technologies__image sections-technologies__image--tech"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.sections-technologies {
  overflow: hidden;

  &__font {
    display: block;

    &--title {
      font-size: em(30);
      font-weight: 800;
      line-height: 100%;

      @include media-breakpoint-down(md) {
        font-size: em(24);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(20);
      }
    }

    &--paragraph {
      font-family: $font-family-second;
      font-size: em(16);
      font-weight: 500;
      line-height: 118%;

      @include media-breakpoint-down(md) {
        font-size: em(13);
      }

      @include media-breakpoint-down(sm) {
        font-size: em(14);
      }
    }
  }

  &__icon {
    &--grid-bullet {
      width: em(35);
      height: em(8);
      opacity: 0.5;

      @include media-breakpoint-down(md) {
        width: em(28);
        height: em(6);
      }
    }

    &--plus {
      position: absolute;
      width: em(40);
      height: em(40);
      pointer-events: none;
      opacity: 0.5;

      @include media-breakpoint-down(sm) {
        width: em(30);
        height: em(30);
      }

      &--1 {
        top: em(0);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(-8);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(7);
          left: em(-6);
        }
      }

      &--2 {
        top: em(60);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(40);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(54);
          left: em(-6);
        }
      }

      &--3 {
        top: em(135);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(100);
          left: em(-4);
        }

        @include media-breakpoint-down(sm) {
          top: em(103);
          left: em(-6);
        }
      }

      &--4 {
        top: em(694);
        left: em(0);

        @include media-breakpoint-down(md) {
          top: em(505);
          left: em(30);
        }

        @include media-breakpoint-down(sm) {
          top: em(605);
          left: em(20);
        }
      }

      &--5 {
        top: em(60);
        left: em(520);

        @include media-breakpoint-down(md) {
          top: em(-8);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(7);
          right: em(-6);
        }
      }

      &--6 {
        top: em(135);
        left: em(520);

        @include media-breakpoint-down(md) {
          top: em(40);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(54);
          right: em(-6);
        }
      }

      &--7 {
        top: em(0);
        left: em(520);

        @include media-breakpoint-down(md) {
          top: em(100);
          right: em(-4);
          left: unset;
        }

        @include media-breakpoint-down(md) {
          top: em(103);
          right: em(-6);
          left: unset;
        }
      }

      &--8 {
        top: em(694);
        left: em(1080);

        @include media-breakpoint-down(md) {
          top: em(505);
          right: em(30);
          left: unset;
        }

        @include media-breakpoint-down(sm) {
          top: em(605);
          right: em(20);
        }
      }
    }
  }

  &__container {
    position: relative;
  }

  &__radial {
    position: absolute;
    opacity: 0.5;

    &--1 {
      top: em(-210);
      left: 50%;
      width: em(1040);
      height: em(362);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #131038 0%,
        rgb(19 16 56 / 0%) 100%
      );
      transform: translateX(-50%);

      @include media-breakpoint-down(md) {
        top: em(-125);
        width: em(448);
        height: em(208);
      }
    }

    &--2 {
      top: em(-360);
      left: 50%;
      width: em(898);
      height: em(455);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #131038 0%,
        rgb(19 16 56 / 0%) 100%
      );
      transform: translateX(-50%);

      @include media-breakpoint-down(md) {
        top: em(-230);
        width: em(410);
        height: em(262);
      }
    }

    &--3 {
      bottom: em(-250);
      left: em(20);
      width: em(756);
      height: em(832);
      background: radial-gradient(
        50% 50% at 50% 50%,
        #ffffff 0%,
        rgb(255 255 255 / 0%) 100%
      );

      @include media-breakpoint-up(md) {
        display: none;
      }

      @include media-breakpoint-down(sm) {
        bottom: em(-200);
        left: em(-100);
      }
    }
  }

  &__body {
    position: relative;
    padding: em(40) em(40) em(90);

    @include media-breakpoint-down(md) {
      padding: em(28) em(33) em(356);
    }

    @include media-breakpoint-down(sm) {
      padding: em(38) em(21) em(213);
    }
  }

  &__caption {
    margin-bottom: em(43);

    @include media-breakpoint-down(md) {
      margin-bottom: em(35);
    }

    @include media-breakpoint-down(sm) {
      margin-bottom: em(28);
    }
  }

  &__title {
    margin-bottom: em(85);
    color: $text-dark-main;

    @include media-breakpoint-down(md) {
      margin-bottom: em(50);
    }

    @include media-breakpoint-down(sm) {
      margin-bottom: em(30);
    }
  }

  &__paragraph {
    color: $text-dark-secondary;

    strong {
      font-weight: 700;
    }

    &--1 {
      width: em(480);
      margin-bottom: em(20);

      @include media-breakpoint-down(md) {
        width: 100%;
        max-width: em(384);
        margin-bottom: em(16);
      }
    }

    &--2 {
      width: em(480);
      margin-bottom: em(20);

      @include media-breakpoint-down(md) {
        width: 100%;
        max-width: em(384);
        margin-bottom: em(16);
      }
    }

    &--3 {
      width: em(420);
      margin-bottom: em(20);

      @include media-breakpoint-down(md) {
        width: 100%;
        max-width: em(336);
        margin-bottom: em(14);
      }
    }

    &--4 {
      width: em(290);
      margin-bottom: em(20);

      @include media-breakpoint-down(md) {
        width: 100%;
        max-width: em(232);
        margin-bottom: em(16);
      }

      @include media-breakpoint-down(sm) {
        width: 100%;
        max-width: em(186);
        margin-bottom: em(16);
      }
    }
  }

  &__footer {
  }

  &__image {
    &--tech {
      position: absolute;
      right: em(40);
      bottom: em(-20);
      width: em(810);
      height: auto;

      @include media-breakpoint-down(md) {
        right: em(-80);
        bottom: 0;
        width: em(589);
      }

      @include media-breakpoint-down(sm) {
        right: em(-70);
        bottom: em(-38);
        width: em(475);
      }
    }
  }
}
</style>
